import type { LayoutPoint } from '../contracts/models';
import { normalizeLayoutPolygon } from './polygons';

export interface SelectedAreaFocusMarker {
  x: number;
  y: number;
  width?: number;
  height?: number;
}

export interface SelectedAreaFocusInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface SelectedAreaFocusTransform {
  scale: number;
  position: { x: number; y: number };
  center: { x: number; y: number };
}

export interface CalculateSelectedAreaFocusTransformOptions {
  points: readonly LayoutPoint[];
  markers?: readonly SelectedAreaFocusMarker[];
  viewport: {
    width: number;
    height: number;
    centerX?: number;
    centerY?: number;
  };
  insets?: Partial<SelectedAreaFocusInsets>;
  minScale?: number;
  maxScale?: number;
}

const finiteInset = (
  value: number | undefined,
): number =>
  value !== undefined && Number.isFinite(value)
    ? Math.max(0, value)
    : 0;

/**
 * Fits a selected AreaTag and its associated markers into a host-provided
 * viewport. The polygon remains the stable camera center while marker bounds
 * expand the required extents.
 */
export const calculateSelectedAreaFocusTransform = ({
  points,
  markers = [],
  viewport,
  insets = {},
  minScale = 0.2,
  maxScale = 4,
}: CalculateSelectedAreaFocusTransformOptions):
  | SelectedAreaFocusTransform
  | null => {
  const polygon = normalizeLayoutPolygon(points);
  if (
    polygon.length < 3 ||
    !Number.isFinite(viewport.width) ||
    !Number.isFinite(viewport.height) ||
    viewport.width <= 0 ||
    viewport.height <= 0
  ) {
    return null;
  }

  const xs = polygon.map(([x]) => x);
  const ys = polygon.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const center = {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
  };
  let requiredHalfWidth = Math.max(1, maxX - minX) / 2;
  let requiredHalfHeight = Math.max(1, maxY - minY) / 2;

  markers.forEach((marker) => {
    if (
      !Number.isFinite(marker.x) ||
      !Number.isFinite(marker.y)
    ) {
      return;
    }
    const halfWidth = Math.max(0, marker.width ?? 0) / 2;
    const halfHeight = Math.max(0, marker.height ?? 0) / 2;
    requiredHalfWidth = Math.max(
      requiredHalfWidth,
      Math.abs(marker.x - halfWidth - center.x),
      Math.abs(marker.x + halfWidth - center.x),
    );
    requiredHalfHeight = Math.max(
      requiredHalfHeight,
      Math.abs(marker.y - halfHeight - center.y),
      Math.abs(marker.y + halfHeight - center.y),
    );
  });

  const resolvedInsets: SelectedAreaFocusInsets = {
    top: finiteInset(insets.top),
    right: finiteInset(insets.right),
    bottom: finiteInset(insets.bottom),
    left: finiteInset(insets.left),
  };
  const availableWidth = Math.max(
    1,
    viewport.width -
      resolvedInsets.left -
      resolvedInsets.right,
  );
  const availableHeight = Math.max(
    1,
    viewport.height -
      resolvedInsets.top -
      resolvedInsets.bottom,
  );
  const lowerScale = Math.min(minScale, maxScale);
  const upperScale = Math.max(minScale, maxScale);
  const scale = Math.min(
    upperScale,
    Math.max(
      lowerScale,
      Math.min(
        availableWidth / (requiredHalfWidth * 2),
        availableHeight / (requiredHalfHeight * 2),
      ),
    ),
  );
  const viewportCenterX =
    viewport.centerX ?? viewport.width / 2;
  const viewportCenterY =
    viewport.centerY ?? viewport.height / 2;
  const safeCenterX =
    viewportCenterX +
    (resolvedInsets.left - resolvedInsets.right) / 2;
  const safeCenterY =
    viewportCenterY +
    (resolvedInsets.top - resolvedInsets.bottom) / 2;

  return {
    scale,
    center,
    position: {
      x: safeCenterX - center.x * scale,
      y: safeCenterY - center.y * scale,
    },
  };
};

export interface SelectedAreaFocusAnchorInput {
  points: readonly LayoutPoint[];
  position: { x: number; y: number };
  scale: number;
  svgRect: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  viewBox?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  containerRect: {
    left: number;
    top: number;
    width: number;
    height: number;
  };
  /**
   * True when svgRect already contains the viewport's pan/zoom transform,
   * as it does for the SVG overlay nested inside PdfPageViewport.
   */
  svgRectIncludesViewportTransform?: boolean;
}

const clamp = (
  value: number,
  min: number,
  max: number,
): number =>
  max < min ? min : Math.min(Math.max(value, min), max);

export const calculateSelectedAreaFocusAnchor = ({
  points,
  position,
  scale,
  svgRect,
  viewBox,
  containerRect,
  svgRectIncludesViewportTransform = false,
}: SelectedAreaFocusAnchorInput):
  | { x: number; y: number }
  | null => {
  const polygon = normalizeLayoutPolygon(points);
  if (
    polygon.length < 3 ||
    svgRect.width <= 0 ||
    svgRect.height <= 0 ||
    !Number.isFinite(scale) ||
    scale === 0
  ) {
    return null;
  }

  const xs = polygon.map(([x]) => x);
  const ys = polygon.map(([, y]) => y);
  const contentX =
    Math.min(...xs) +
    (Math.max(...xs) - Math.min(...xs)) / 2;
  const contentY = Math.min(...ys);
  const transformedX = svgRectIncludesViewportTransform
    ? contentX
    : contentX * scale + position.x;
  const transformedY = svgRectIncludesViewportTransform
    ? contentY
    : contentY * scale + position.y;
  const hasViewBox =
    viewBox != null &&
    viewBox.width > 0 &&
    viewBox.height > 0;
  const screenPoint = hasViewBox
    ? (() => {
        const meetScale = Math.min(
          svgRect.width / viewBox.width,
          svgRect.height / viewBox.height,
        );
        const renderedWidth = viewBox.width * meetScale;
        const renderedHeight = viewBox.height * meetScale;
        const offsetX =
          svgRect.left +
          (svgRect.width - renderedWidth) / 2;
        const offsetY =
          svgRect.top +
          (svgRect.height - renderedHeight) / 2;
        return {
          x:
            offsetX +
            (transformedX - viewBox.x) * meetScale,
          y:
            offsetY +
            (transformedY - viewBox.y) * meetScale,
        };
      })()
    : {
        x: svgRect.left + transformedX,
        y: svgRect.top + transformedY,
      };

  return {
    x: clamp(
      screenPoint.x - containerRect.left,
      16,
      containerRect.width - 16,
    ),
    y: clamp(
      screenPoint.y - containerRect.top - 12,
      52,
      containerRect.height - 12,
    ),
  };
};
